import { arraysEqualIgnoreOrder, BaseComparisonRes, compareArrays, pushIfNew } from "./collection.tools";
import { anyReallyAny } from "./object.tools";

/**
 * Default ID that can be used when creating new objects (before a tmp id is set)
 */
export const INIT_ID = Number.MIN_SAFE_INTEGER;

export type StringKeys<T> = keyof T;

export interface CompareDTOFieldsResValues {

    /** Old value of dto */
    oldValue: unknown;

    /** New value of dto */
    newValue: unknown;
}

export interface CompareDTOFieldsRes<T> {

    /** New object */
    newObject: T;

    /** Old object */
    oldObject: T;

    /** Fields updated, Record<field, {oldValue:any, newValue:any} */
    fieldsToUpdate: Record<StringKeys<T>, CompareDTOFieldsResValues>;
}

export interface ComparisonDtoRes<T> extends BaseComparisonRes<T> {

    /** Elements updated in newArray from oldArray */
    toUpdate: CompareDTOFieldsRes<T>[];

    /** Elements rejected of comparison */
    toReject: T[];
}


export interface CompareKeys<T> {

    /** 
     * List of common fields of elements, used to check if two given elements should be compared
     * StringKeys<T>[], use value to match objects with (ex: id, uid, name+surname,...)
     */
    uniqueFields?: StringKeys<T>[];

    /** List of keys to ignore for comparison */
    ignoredFields?: StringKeys<T>[];
}

export interface CompareFields<T> extends CompareKeys<T> {

    /** List of field to compare */
    fields: StringKeys<T>[];
}

export interface CompareDtoArraysParams<T> extends CompareFields<T> {

    /** Compared with newData */
    oldData: T[];

    /** Compared with oldData */
    newData: T[];

    /** Fields mandatory for compare, if mandatory field value isn't defined, element is rejected */
    mandatoryFields?: StringKeys<T>[];

    /** Marker of delete/archived fields */
    deletionMarkerFields?: StringKeys<T>[];
}

/**
 * Compare two DTOs arrays
 * @returns an object of result of comparison
 */
export function compareDtoArrays<T>({
    oldData,
    newData,
    mandatoryFields,
    fields,
    uniqueFields,
    ignoredFields,
    deletionMarkerFields
}: CompareDtoArraysParams<T>): ComparisonDtoRes<T> {
    // First, check if all mandatoryFields have been set for res.newData. If not, save the line as a rejected one and do not send it for comparison updates.
    const linesRejected: T[] = [];
    let newDataWithNotRejectData: T[] = [];

    if (mandatoryFields) {
        for (const data of newData) {
            let rejected = false;
            for (const mandatoryField of mandatoryFields) {
                const value: anyReallyAny = data[mandatoryField];
                if (value == null || value === "") {
                    pushIfNew(linesRejected, data);
                    rejected = true;
                    break;
                }
            }
            if (!rejected) {
                newDataWithNotRejectData.push(data);
            }
        }
    } else {
        newDataWithNotRejectData = newData;
    }

    const updateRes: CompareDTOFieldsRes<T>[] = [];

    const compareRes = compareArrays({
        oldArray: oldData,
        newArray: newDataWithNotRejectData,
        matchingElementsFct: (dto1: T, dto2: T) => {
            return areDTOMatching(dto1, dto2, { uniqueFields, ignoredFields });
        },
        needsUpdateFct: (dto1: T, dto2: T) => {
            const updateCompare = compareDTOFields(dto1, dto2, { fields, uniqueFields, ignoredFields });// tableColumnFields always "extends" (keyof T);

            if (updateCompare) {
                return pushIfNew(updateRes, updateCompare);
            }
            return false;
        },
        canBeRemovedFct: (a: T) => {
            return canDTOBeRemoved(a) && !Boolean(deletionMarkerFields?.some(deletionMarker => !a[deletionMarker]));// not removable if already removed...
        }
    });

    return {
        toAdd: compareRes.toAdd,
        toKeep: compareRes.toKeep,
        toRemove: compareRes.toRemove,
        toUpdate: updateRes,
        toReject: linesRejected
    };
}


/**
 * Check if two given DTOs are matching
 * 
 * @param c1 first dto to compare
 * @param c2 second dto to compare
 * @returns true if both DTOs are matching
 */
export function areDTOMatching<T>(c1: T, c2: T, { uniqueFields, ignoredFields }: CompareKeys<T>): boolean {
    if (!uniqueFields?.length) { return false; }

    for (const key of uniqueFields) {

        // Don't check key if ignored
        if (ignoredFields?.includes(key)) { continue; }

        if (c1[key] != c2[key]) { return false; }
    }
    return true;
}


/**
 * Compare field of two DTOs
 *
 * @param dto1 first dto to compare
 * @param dto2 second dto to compare
 * @returns null if dtos are equal, comparisonRes if a difference is found
 */
export function compareDTOFields<T>(dto1: T, dto2: T, { fields, uniqueFields, ignoredFields }: CompareFields<T>): CompareDTOFieldsRes<T> | null {
    // User modified data are overwritten except for some values
    if (uniqueFields?.length && !areDTOMatching(dto1, dto2, { uniqueFields, ignoredFields }) || dto1 == null || dto2 == null) { return null; }

    const CompareDTOFieldsRes: CompareDTOFieldsRes<T> = {
        oldObject: dto1,
        newObject: dto2,
        fieldsToUpdate: {} as Record<StringKeys<T>, CompareDTOFieldsResValues>
    };

    for (const key of fields) {
        if (ignoredFields && ignoredFields.length > 0 && ignoredFields.includes(key)) { continue; }

        const dto1Val = dto1[key];
        const dto2Val = dto2[key];
        if (Array.isArray(dto1Val) && Array.isArray(dto2Val)) {
            if (!arraysEqualIgnoreOrder(dto1Val, dto2Val)) {
                CompareDTOFieldsRes.fieldsToUpdate[key] = {
                    oldValue: dto1Val,
                    newValue: dto2Val
                };
            }
        } else if (dto1Val != dto2Val) {
            CompareDTOFieldsRes.fieldsToUpdate[key] = {
                oldValue: dto1Val,
                newValue: dto2Val
            };
        }
    }

    const isFieldsUpdated = Object.keys(CompareDTOFieldsRes.fieldsToUpdate).length > 0;
    return isFieldsUpdated ? CompareDTOFieldsRes : null;
}

/** Default canBeRemoved function for CompareDtoArrays */
export function canDTOBeRemoved<T>(c: T): boolean {
    return true;
}